import React from 'react'
import toast from 'react-hot-toast'
import { Confetti, X } from '@phosphor-icons/react'
import { Icon, IconButton } from '@shopflo/ui'
import { classNames } from 'src/lib/utils/helpers'

interface SuccessNudgeToastOptions {
  title: string
  description?: string
  duration?: number
}

/**
 * Bottom-right celebratory card (title + description + dismiss) for milestones like a plan upgrade —
 * heavier than successToast, which is a one-line pill. Rendered by the app-level <Toaster /> in _app.tsx.
 */
export const successNudgeToast = ({ title, description, duration = 5000 }: SuccessNudgeToastOptions) => {
  toast.custom(
    t => (
      <div
        className={classNames(
          'flex w-[480px] max-w-[calc(100vw-2rem)] items-start gap-3 rounded-2xl border-2 border-emerald-200 bg-white py-3 pl-5 pr-4',
          'shadow-[0_0_0_1px_rgba(9,9,11,0.08),0_16px_12px_-6px_rgba(9,9,11,0.06),0_2px_10px_-6px_rgba(9,9,11,0.48)]',
          // fill-mode-forwards: tailwindcss-animate's exit doesn't hold its last frame, so without it the card snaps back
          // to visible after the 200ms fade and stays until react-hot-toast unmounts it.
          t.visible
            ? 'animate-in fade-in slide-in-from-bottom-2 duration-300'
            : 'animate-out fade-out slide-out-to-right-2 duration-200 fill-mode-forwards pointer-events-none'
        )}
      >
        <Icon IconComponent={Confetti} size='xl' className='mt-0.5 flex-shrink-0 text-emerald-500' />
        <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <p className='text-text-md-semibold text-zinc-800'>{title}</p>
          {description && <p className='text-text-sm-regular text-zinc-500'>{description}</p>}
        </div>
        <IconButton
          variant='ghost'
          size='sm'
          IconComponent={X}
          aria-label='Dismiss'
          className='flex-shrink-0 self-center text-zinc-500'
          onClick={() => toast.dismiss(t.id)}
        />
      </div>
    ),
    // Unmount right after the 200ms exit instead of react-hot-toast's default 1s.
    { duration, position: 'bottom-right', removeDelay: 200 }
  )
}
